import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';

const LabModal = ({ show, onHide, onLabSaved, editLab }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact: {
      phone: '',
      email: ''
    },
    defaultCollector: '',
    isActive: true
  });
  const [collectors, setCollectors] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCollectors();
      if (editLab) {
        setFormData({
          name: editLab.name || '',
          address: editLab.address || '',
          contact: {
            phone: editLab.contact?.phone || '',
            email: editLab.contact?.email || ''
          },
          defaultCollector: editLab.defaultCollector?._id || editLab.defaultCollector || '',
          isActive: editLab.isActive !== undefined ? editLab.isActive : true
        });
      } else {
        resetForm();
      }
    }
  }, [show, editLab]);

  const fetchCollectors = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/collectors?isActive=true`);
      setCollectors(response.data);
    } catch (error) {
      console.error('Error fetching collectors:', error);
      toast.error('Failed to load collectors');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      contact: {
        phone: '',
        email: ''
      },
      defaultCollector: '',
      isActive: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        contact: {
          phone: formData.contact.phone.trim(),
          email: formData.contact.email.trim()
        }
      };

      let response;
      if (editLab) {
        response = await axios.put(`${config.API_BASE_URL}/labs/${editLab._id}`, dataToSend);
      } else {
        response = await axios.post(`${config.API_BASE_URL}/labs`, dataToSend);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        onLabSaved();
        onHide();
      }
    } catch (error) {
      console.error('Error saving lab:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save lab';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: {
        ...prev.contact,
        [field]: value
      }
    }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editLab ? 'Edit Lab' : 'Add New Lab'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Lab Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter lab name"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => handleContactChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  placeholder="Enter email address"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Default Collector</Form.Label>
                <Form.Select
                  value={formData.defaultCollector}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultCollector: e.target.value }))}
                >
                  <option value="">Select a collector</option>
                  {collectors.map(collector => (
                    <option key={collector._id} value={collector._id}>
                      {collector.name} - {collector.mobile}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Address *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter lab address"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {editLab && (
            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Active"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !formData.name.trim() || !formData.address.trim()}
          >
            {saving ? 'Saving...' : (editLab ? 'Update Lab' : 'Add Lab')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LabModal;
